import React from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { BLOG_POSTS, BLOG_CATEGORIES } from "@/lib/blogPosts";

const BlogPage = () => {
  const [featured, ...rest] = BLOG_POSTS;

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="Resources & Insights — Airbnb Management Blog"
        description="Expert insights on Airbnb management, short-term rental tips, VA & DC STR regulations, and revenue optimization from JNN Homes."
        canonical="https://www.jnnhomes.com/blog"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "JNN Homes Resources & Insights",
          url: "https://www.jnnhomes.com/blog",
          publisher: {
            "@type": "Organization",
            name: "JNN Homes",
            url: "https://www.jnnhomes.com",
          },
        }}
      />
      <Navbar />

      <main className="pt-28 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-12 space-y-4">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-primary">
              Resources & Insights
            </span>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
              VA & DC STR Insights
            </h1>
            <p className="text-lg text-muted-foreground">
              Practical guidance on Airbnb management, local regulations, and
              revenue optimization for VA & DC property owners.
            </p>
          </div>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {BLOG_CATEGORIES.map((cat) => (
              <span
                key={cat}
                className="px-3 py-1.5 rounded-full bg-muted/60 border border-border/60 text-xs font-medium text-muted-foreground"
              >
                {cat}
              </span>
            ))}
          </div>

          {/* Featured article */}
          <Link
            to={`/blog/${featured.slug}`}
            className="group block rounded-2xl overflow-hidden border border-border/60 shadow-sm hover:shadow-2xl transition-all duration-500 mb-12"
          >
            <div className="grid md:grid-cols-2 gap-0">
              <div className="relative h-64 md:h-auto min-h-[280px]">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <span className="text-xs font-bold uppercase tracking-widest text-primary mb-3">
                  {featured.category}
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4 leading-tight">
                  {featured.title}
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {featured.excerpt}
                </p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />{" "}
                    {new Date(featured.date).toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {featured.readTime}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1.5 text-primary font-bold text-sm mt-6 group-hover:gap-2.5 transition-all">
                  Read Article <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>

          {/* Article grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group flex flex-col rounded-2xl overflow-hidden border border-border/60 shadow-sm hover:shadow-xl transition-all duration-300 bg-card"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <span className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
                    {post.category}
                  </span>
                  <h3 className="text-lg font-heading font-bold text-foreground mb-3 leading-snug">
                    {post.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-grow">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground pt-4 border-t border-border/60">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />{" "}
                      {new Date(post.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {post.readTime}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
